/** Optional motion adapter. Horizon owns the drawer; Alpine owns menu state and focus. */
class VoltHeaderNavigationMotion extends HTMLElement {
  animations = new Set();
  audienceAnimations = new Set();
  frame = 0;
  pointerInput = false;
  audienceHintConsumed = false;

  connectedCallback() {
    this.host = this.parentElement;
    this.details = this.host.querySelector('details');
    this.drawer = this.host.querySelector('.component-header-navigation__drawer');
    this.audiences = this.host.querySelector('.component-header-navigation__audiences');
    if (!this.details || !this.drawer) return;
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.open = this.details.classList.contains('menu-open');
    this.submenu = this.host.querySelector('.component-header-navigation__submenu');
    this.host.dataset.navigationInput = 'keyboard';
    this.host.addEventListener('pointerdown', this.onPointer, true);
    this.host.addEventListener('keydown', this.onKeyboard, true);
    this.host.addEventListener('focusin', this.onAudienceInteraction, true);
    this.host.addEventListener('wheel', this.onWheel, true);
    this.audiences?.addEventListener('scroll', this.onAudienceInteraction);
    window.addEventListener('resize', this.onResize);
    this.reducedMotion.addEventListener('change', this.onMotionPreference);
    this.observer = new MutationObserver(this.schedule);
    this.observer.observe(this.details, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
    this.cancel();
    this.host?.removeEventListener('pointerdown', this.onPointer, true);
    this.host?.removeEventListener('keydown', this.onKeyboard, true);
    this.host?.removeEventListener('focusin', this.onAudienceInteraction, true);
    this.host?.removeEventListener('wheel', this.onWheel, true);
    this.audiences?.removeEventListener('scroll', this.onAudienceInteraction);
    window.removeEventListener('resize', this.onResize);
    this.reducedMotion?.removeEventListener('change', this.onMotionPreference);
  }

  onPointer = event => {
    this.onAudienceInteraction(event);
    this.pointerInput = true;
    this.host.dataset.navigationInput = 'pointer';
    // A new action takes priority over an unfinished decorative entrance.
    this.cancel();
  };

  onKeyboard = () => {
    this.pointerInput = false;
    this.host.dataset.navigationInput = 'keyboard';
    this.cancel();
  };

  onMotionPreference = () => {
    if (this.reducedMotion.matches) this.cancel();
  };

  onAudienceInteraction = event => {
    if (!event || !this.audiences?.contains(event.target)) return;
    this.audienceHintConsumed = true;
    this.cancelAudienceHint();
  };

  onResize = () => this.cancelAudienceHint();

  onWheel = event => {
    this.onAudienceInteraction(event);
    this.cancelAudienceHint();
  };

  cancelAudienceHint() {
    for (const animation of this.audienceAnimations) animation.cancel();
    this.audienceAnimations.clear();
  }

  schedule = () => {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(this.reconcile);
  };

  reconcile = () => {
    this.frame = 0;
    const open = this.details.classList.contains('menu-open');
    const submenu = this.host.querySelector('.component-header-navigation__submenu');
    const opening = open && !this.open;
    const changedPanel = submenu !== this.submenu;
    this.open = open;
    this.submenu = submenu;

    if (!open || !this.pointerInput || this.reducedMotion.matches) {
      this.cancel();
      return;
    }
    if (opening && this.dataset.entrance === 'true') {
      this.enterMenu();
    } else if (changedPanel && this.dataset.drilldown === 'true') {
      const panel = submenu || this.host.querySelector('.component-header-navigation__panel');
      if (panel) this.animate(panel, { x: submenu ? 16 : -16, startOpacity: 0.65 });
    }
    if (opening) this.hintAudiences();
  };

  hintAudiences() {
    const strip = this.audiences;
    if (this.dataset.audienceHint !== 'true' || this.audienceHintConsumed || !strip?.clientWidth) return;
    const overflow = strip.scrollWidth - strip.clientWidth;
    if (overflow <= 1 || Math.abs(strip.scrollLeft) > 1) return;
    this.audienceHintConsumed = true;
    const distance = Math.min(24, overflow) * (getComputedStyle(strip).direction === 'rtl' ? 1 : -1);
    // Hint only with child transforms; native scrolling and selection stay untouched.
    for (const tab of strip.children) {
      this.track(tab.animate([
        { transform: 'translateX(0)', offset: 0, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
        { transform: `translateX(${distance}px)`, offset: 0.35, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
        { transform: 'translateX(0)', offset: 1 },
      ], {
        // A one-time discovery cue: quick outward movement, gentler return.
        duration: 620,
        delay: 650,
        easing: 'linear',
      }), true);
    }
  }

  enterMenu() {
    const bounds = this.drawer.getBoundingClientRect();
    const items = this.host.querySelectorAll([
      '.component-header-navigation__audiences',
      '.component-header-navigation__promo',
      '.component-header-navigation__category',
      '.component-header-navigation__features',
      '.component-header-navigation__footer',
    ].join(','));
    // Only animate what is initially visible; scrolling must not reveal delayed items.
    const visible = [...items].filter(item => {
      const rect = item.getBoundingClientRect();
      return rect.height && rect.bottom > bounds.top && rect.top < bounds.bottom;
    });
    const step = Math.min(28, 112 / Math.max(1, visible.length - 1));
    visible.forEach((item, index) => this.animate(item, {
      y: 12,
      delay: 35 + index * step,
      duration: 280,
      startOpacity: 0.15,
    }));
  }

  animate(element, { x = 0, y = 0, delay = 0, duration = 200, startOpacity = 0.65 } = {}) {
    const easing = getComputedStyle(this.host).getPropertyValue('--ease-out-quad').trim() || 'cubic-bezier(0.32, 0.72, 0, 1)';
    const animation = element.animate([
      { transform: `translate(${x}px, ${y}px)`, opacity: startOpacity },
      { transform: 'translate(0, 0)', opacity: 1 },
    ], { duration, delay, easing, fill: 'backwards' });
    this.track(animation);
  }

  track(animation, audienceHint = false) {
    this.animations.add(animation);
    if (audienceHint) this.audienceAnimations.add(animation);
    const remove = () => {
      this.animations.delete(animation);
      this.audienceAnimations.delete(animation);
    };
    animation.addEventListener('finish', remove, { once: true });
    animation.addEventListener('cancel', remove, { once: true });
  }

  cancel() {
    for (const animation of this.animations) animation.cancel();
    this.animations.clear();
    this.audienceAnimations.clear();
  }
}

if (!customElements.get('volt-header-navigation-motion')) {
  customElements.define('volt-header-navigation-motion', VoltHeaderNavigationMotion);
}
